import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import numeral from 'numeral';
import { Helmet } from 'react-helmet';
import snakeCase from 'lodash/snakeCase';

import { completeCourse } from '../redux/actions';
import { selectCourse } from '../redux/selectors';
import CoursePreviewModal from '../components/CoursePreviewModal';
import CourseEnrollModal from '../components/CourseEnrollModal';
import SixtyDayWarningModal from '../components/SixtyDayWarningModal';
import Icon from '../../../common/icons/Icon';
import { sendIntercomEvent } from '../../../common/events';

class CourseOverview extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    course: PropTypes.object.isRequired,
  };

  componentDidMount() {
    const { course, dispatch } = this.props;
    if (!course.complete) {
      const hasCompletedCourse = course.chapters.reduce((prev, curr) => (prev === false ? false : curr.complete), true);
      if (hasCompletedCourse) {
        dispatch(completeCourse(course.id));
      }
    }
    sendIntercomEvent(`viewed_${snakeCase(course.name)}`);
  }

  renderList = nextLesson => {
    const { course, match } = this.props;
    let current = false;
    const enrolled = course.enrolled;
    return (
      <div className="course-page__chapter-list">
        {course.chapters.map((chapter, i) => {
          let wrapperClassName = 'chapter-list-item';
          let statusIcon;
          let toLesson = `${match.url}/chapters/${chapter.id}`;

          const lessonString = `${chapter.lessons} lesson${chapter.lessons === 1 ? '' : 's'}`;
          const quizString = `${chapter.quizzes} quiz${chapter.quizzes === 1 ? '' : 'zes'}`;

          if (chapter.complete && enrolled) {
            statusIcon = (
              <div className="chapter--status-icon">
                <Icon className="icon--complete" icon="CheckmarkCircleOutline" />
              </div>
            );
          } else if (!chapter.complete && !current && enrolled) {
            current = true;
            toLesson += `/lessons/${nextLesson.id}?autoplay`;
            statusIcon = (
              <div className="chapter--status-icon">
                <Icon icon="PlayCircle" />
              </div>
            );
          } else {
            statusIcon = <Icon icon="Lock" />;
            wrapperClassName = 'chapter-list-item chapter-list-item--locked';
            return (
              <div key={chapter.id} className={wrapperClassName}>
                <div className="chapter-list-item__header">
                  <h2>Chapter {i + 1}</h2>
                  {statusIcon}
                </div>
                <div className="chapter-list-item__name">
                  <h1>{chapter.name}</h1>
                  <span>
                    {lessonString} | {quizString}
                  </span>
                </div>
                <p>{chapter.description}</p>
              </div>
            );
          }

          return (
            <Link to={toLesson} key={chapter.id} className={wrapperClassName}>
              <div className="chapter-list-item__header">
                <h2>Chapter {i + 1}</h2>
                {statusIcon}
              </div>
              <div className="chapter-list-item__name">
                <h1>{chapter.name}</h1>
                <span>
                  {lessonString} | {quizString}
                </span>
              </div>
              <p>{chapter.description}</p>
            </Link>
          );
        })}
      </div>
    );
  };

  renderCourseInfo = nextChapter => {
    const { course } = this.props;
    return (
      <div className="course-page__info">
        {!course.enrolled ? (
          <div className="course-page__info__padding">
            <div className="course-page__info__enroll">
              <h1>{course.name}</h1>
              <div className="course-page__enroll-price">
                <h2>{numeral(course.price / 100).format('$0,0.00')}</h2>
                <span>- Start Training Today!</span>
              </div>
              <CourseEnrollModal course={course} nextChapterId={nextChapter.id} />
            </div>
          </div>
        ) : null}
        <div className="course-page__info__padding">
          <h1>About the Course</h1>
          <div className="long-description">
            <p>{course.long_description}</p>
          </div>
          {course.badge_id ? (
            <div className="course-page__badge-info">
              <h1>Complete this course to unlock</h1>
              <div className="course-page__info__header">
                <img src={course.badge.image.location} alt={course.badge.name} />
                <div className="instructor-info">
                  <h2>{course.name} Badge</h2>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  renderHero = (nextChapter, chapterLessons, nextLesson) => {
    const { course, match } = this.props;
    return (
      <div
        className="course-page__hero__wrapper"
        style={course.image ? { backgroundImage: `url(${course.image.location})` } : {}}
      >
        <div className="container">
          <Link to={`/subscription/courses`}>
            <span className="button__back-button">
              <Icon icon="BackArrow" />Back to Courses
            </span>
          </Link>
          <div className="course-page__hero-box">
            <h1>{course.name}</h1>
            <p>{course.description}</p>
            <div className="course-page__hero-box__buttons">
              {course.enrolled ? (
                <Link to={`${match.url}/chapters/${nextChapter.id}/lessons/${nextLesson.id}?autoplay`}>
                  <span className="button">{course.progress ? 'Resume' : 'Begin'}</span>
                </Link>
              ) : (
                <CourseEnrollModal course={course} nextChapterId={nextChapter.id} />
              )}
              {course.video_id ? (
                <CoursePreviewModal
                  courseId={course.id}
                  courseImage={
                    course.image ? course.image.location : 'https://d27vybgvtr1dba.cloudfront.net/placeholder.jpg'
                  }
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { course } = this.props;
    const nextChapter =
      course.chapters.find(chapter => !chapter.complete) || course.chapters[course.chapters.length - 1];
    const chapterLessons = course.lessons.filter(lesson => lesson.chapter_id === nextChapter.id);
    const nextLesson = chapterLessons.find(lesson => !lesson.complete) || chapterLessons[chapterLessons.length - 1];

    return (
      <div>
        <Helmet>
          <title>{course.name}</title>
        </Helmet>
        <SixtyDayWarningModal course={course} />
        {this.renderHero(nextChapter, chapterLessons, nextLesson)}
        <div className="course-page__chapter-list__wrapper">
          <div className="container">
            <h3 className="course-page__title">Course Overview</h3>
            <div className="chapter-list__section">
              {this.renderList(nextLesson)}
              {this.renderCourseInfo(nextChapter)}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => ({
  course: selectCourse(state, props),
});

export default connect(mapStateToProps)(CourseOverview);



// WEBPACK FOOTER //
// ./src/subscription/courses/views/CourseOverview.js